import type { PickupLocation } from '@otw/models'
import type { Terminal } from '@otw/models/leads/terminals'
import { isPickupLocationAddressComplete } from '@otw/models/leads/leadShippingValidation'

/** Address fields the terminal form collects, written through to pickup_locations. */
export interface TerminalAddressInput {
  address: string
  city: string
  state: string
  zip: string
}

/**
 * Build a pickup_locations payload from terminal address fields.
 * Only sets `company_name` when the terminal supplies a label, so adopting an
 * existing address never clobbers a meaningful company name.
 */
export function buildTerminalLocationPayload(
  input: TerminalAddressInput,
  terminalLabel: string,
): Omit<PickupLocation, 'id' | 'created_at' | 'updated_at'> {
  const label = terminalLabel.trim()
  return {
    address: input.address.trim(),
    city: input.city.trim(),
    state: input.state.trim(),
    zip: input.zip.trim(),
    ...(label ? { company_name: label } : {}),
  }
}

export function resolveTerminalLocation(
  terminal: Terminal,
  locations: PickupLocation[] | undefined,
): PickupLocation | undefined {
  const id = terminal.pickup_location_id?.trim()
  if (!id || !locations?.length) return undefined
  return locations.find((loc) => loc.id === id)
}

export function isTerminalAddressComplete(
  terminal: Terminal,
  locations: PickupLocation[] | undefined,
): boolean {
  return isPickupLocationAddressComplete(resolveTerminalLocation(terminal, locations))
}
